import React, { FC } from "react";
import { Controller, useFormContext } from "react-hook-form";
import Box from "@mui/material/Box";
import Autocomplete from "@mui/material/Autocomplete";
import Popper, { PopperProps } from "@mui/material/Popper";
import TextField from "@mui/material/TextField";
import { classNames } from "repoV2/utils/common/render/classNames";
import ErrorMessage from "../ErrorMessage/ErrorMessage";
import {
    IExlySearchableDropdown,
    IExlySearchableDropdownOption,
} from "../ExlySearchableDropdown/ExlySearchableDropdown.interfaces";
import styles from "../ExlySearchableDropdown/ExlySearchableDropdown.module.scss";

const PopperComponent: FC<PopperProps> = (props: PopperProps) => {
    return <Popper {...props} />;
};

/**
 * Note that here, the selected value will be the complete option object
 */
const ExlySearchableDropdownV2: FC<IExlySearchableDropdown> = props => {
    const {
        name,
        options,
        placeholder,
        classes,
        onValueChange,
        ExpandIcon,
        registerOptions = {},
        inputVariant = "outlined",
        menuPlacement,
        CustomErrorMessageComponent,
        renderOption,
        onChange,
        disabled,
    } = props;

    const {
        formState: { errors },
        control,
    } = useFormContext();

    const error = name ? errors[name] : null;
    const showError = !!error;
    const { message: errorMessage } = error || {};

    return (
        <>
            <Controller
                name={`${name}`}
                control={control}
                rules={registerOptions}
                render={({
                    field: { onChange: onFieldChange, value, ref },
                }) => (
                    <Autocomplete
                        multiple={false}
                        disableClearable
                        autoComplete
                        disablePortal
                        blurOnSelect
                        disabled={disabled}
                        inputValue={value?.selectedValue}
                        autoHighlight
                        value={value}
                        classes={classes}
                        fullWidth
                        popupIcon={ExpandIcon}
                        {...(renderOption && {
                            renderOption: (
                                _props,
                                option: IExlySearchableDropdownOption
                            ) => (
                                <Box component="li" {..._props}>
                                    {renderOption(option)}
                                </Box>
                            ),
                        })}
                        renderInput={params => (
                            <TextField
                                {...params}
                                error={showError}
                                inputRef={ref}
                                variant={inputVariant}
                                placeholder={placeholder}
                                className={classes?.textInput}
                            />
                        )}
                        PopperComponent={popperProps => (
                            <PopperComponent
                                {...popperProps}
                                className={classNames(
                                    classes?.popper,
                                    styles.popperBase
                                )}
                                placement={menuPlacement}
                            />
                        )}
                        options={options}
                        onChange={(_event, newValue) => {
                            if (onValueChange) {
                                onValueChange(newValue);
                                return;
                            }
                            onFieldChange(newValue); // inform react-hook-form of the new value
                            if (onChange) onChange(newValue); // additionally call the props.onChange if provided
                        }}
                    />
                )}
            />
            <ErrorMessage
                showError={showError}
                errorMessage={errorMessage}
                classes={classes}
                CustomErrorMessageComponent={CustomErrorMessageComponent}
            />
        </>
    );
};

export default ExlySearchableDropdownV2;
